import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDemo } from '../../state/DemoContext';
import { INVESTOR_TYPES, GEOGRAPHIC_REGIONS, ASSET_TYPES } from '../../utils/constants';
import { formatCurrency } from '../../utils/helpers';
import './investorsignup.css';

const InvestorSignup = () => {
  const history = useHistory();
  const { actions } = useDemo();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    email: '',
    type: 'Individual',
    
    // Profile Information
    description: '',
    experience: '',
    aum: '',
    headquarters: '',
    
    // Investment Mandates
    assetTypes: ['Property'],
    maxLtv: 0.65,
    minTicket: 100000,
    maxTicket: 2000000,
    geos: ['TX'],
    lienPreference: 'FirstLien',
    termRange: [12, 36],
    minCapRate: 0.05
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = () => {
    const investor = {
      id: `inv-${Date.now()}`,
      name: formData.name,
      email: formData.email,
      type: formData.type,
      status: 'Active',
      mandates: {
        assetTypes: formData.assetTypes,
        maxLtv: formData.maxLtv,
        minTicket: formData.minTicket,
        maxTicket: formData.maxTicket,
        geos: formData.geos,
        lienPreference: formData.lienPreference,
        termRange: formData.termRange,
        minCapRate: formData.minCapRate
      },
      profile: {
        description: formData.description,
        experience: formData.experience,
        aum: parseFloat(formData.aum) || 0,
        headquarters: formData.headquarters
      },
      createdAt: new Date().toISOString()
    };

    actions.signupInvestor(investor);
    actions.showToast('Investor account created successfully!', 'success');
    history.push('/jo-xyz-demo/investor');
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.name && formData.email && formData.type;
      case 2:
        return formData.description && formData.experience && formData.headquarters;
      case 3:
        return formData.assetTypes.length > 0 && formData.geos.length > 0;
      default:
        return false;
    }
  };

  const renderStep1 = () => (
    <div className="jo-xyz-demo-signup-step">
      <h3>Basic Information</h3>
      <p>Tell us about your organization and contact details.</p>
      
      <div className="jo-xyz-demo-form-grid">
        <div className="jo-xyz-demo-form-group">
          <label>Organization Name *</label>
          <input
            type="text"
            className="jo-xyz-demo-input"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., Acme Capital Partners"
          />
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Email Address *</label>
          <input
            type="email"
            className="jo-xyz-demo-input"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="investor@example.com"
          />
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Investor Type *</label>
          <select
            className="jo-xyz-demo-select jo-xyz-demo-input"
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
          >
            {INVESTOR_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="jo-xyz-demo-signup-step">
      <h3>Profile Information</h3>
      <p>Share details about your investment experience and organization.</p>
      
      <div className="jo-xyz-demo-form-grid">
        <div className="jo-xyz-demo-form-group jo-xyz-demo-form-group--full">
          <label>Organization Description *</label>
          <textarea
            className="jo-xyz-demo-input"
            rows="3"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe your investment focus and expertise..."
          />
        </div>
        
        <div className="jo-xyz-demo-form-group jo-xyz-demo-form-group--full">
          <label>Investment Experience *</label>
          <input
            type="text"
            className="jo-xyz-demo-input"
            value={formData.experience}
            onChange={(e) => handleInputChange('experience', e.target.value)}
            placeholder="e.g., 10+ years in commercial real estate"
          />
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Assets Under Management</label>
          <input
            type="number"
            className="jo-xyz-demo-input"
            value={formData.aum}
            onChange={(e) => handleInputChange('aum', e.target.value)}
            placeholder="0"
            min="0"
            step="1000000"
          />
          <div className="jo-xyz-demo-form-help">In USD</div>
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Headquarters *</label>
          <input
            type="text"
            className="jo-xyz-demo-input"
            value={formData.headquarters}
            onChange={(e) => handleInputChange('headquarters', e.target.value)}
            placeholder="e.g., New York, NY"
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="jo-xyz-demo-signup-step">
      <h3>Investment Mandates</h3>
      <p>Define your investment criteria and preferences.</p>
      
      <div className="jo-xyz-demo-form-grid">
        <div className="jo-xyz-demo-form-group jo-xyz-demo-form-group--full">
          <label>Asset Types *</label>
          <div className="jo-xyz-demo-checkbox-group">
            {ASSET_TYPES.map(type => (
              <label key={type} className="jo-xyz-demo-checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.assetTypes.includes(type)}
                  onChange={(e) => handleArrayChange('assetTypes', type, e.target.checked)}
                />
                <span className="jo-xyz-demo-checkbox-label">{type}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Maximum LTV</label>
          <input
            type="range"
            min="0.3"
            max="0.8"
            step="0.05"
            value={formData.maxLtv}
            onChange={(e) => handleInputChange('maxLtv', parseFloat(e.target.value))}
            className="jo-xyz-demo-range-slider"
          />
          <div className="jo-xyz-demo-range-value">{(formData.maxLtv * 100).toFixed(0)}%</div>
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Minimum Ticket Size</label>
          <input
            type="number"
            className="jo-xyz-demo-input"
            value={formData.minTicket}
            onChange={(e) => handleInputChange('minTicket', parseInt(e.target.value))}
            min="10000"
            step="10000"
          />
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Maximum Ticket Size</label>
          <input
            type="number"
            className="jo-xyz-demo-input"
            value={formData.maxTicket}
            onChange={(e) => handleInputChange('maxTicket', parseInt(e.target.value))}
            min="100000"
            step="100000"
          />
        </div>
        
        <div className="jo-xyz-demo-form-group jo-xyz-demo-form-group--full">
          <label>Geographic Focus *</label>
          <div className="jo-xyz-demo-checkbox-group">
            {GEOGRAPHIC_REGIONS.map(region => (
              <label key={region} className="jo-xyz-demo-checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.geos.includes(region)}
                  onChange={(e) => handleArrayChange('geos', region, e.target.checked)}
                />
                <span className="jo-xyz-demo-checkbox-label">{region}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Lien Preference</label>
          <select
            className="jo-xyz-demo-select jo-xyz-demo-input"
            value={formData.lienPreference}
            onChange={(e) => handleInputChange('lienPreference', e.target.value)}
          >
            <option value="FirstLien">First Lien Only</option>
            <option value="SecondLien">Second Lien Only</option>
            <option value="Any">Any Position</option>
          </select>
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Minimum Cap Rate</label>
          <input
            type="range"
            min="0.03"
            max="0.12"
            step="0.01"
            value={formData.minCapRate}
            onChange={(e) => handleInputChange('minCapRate', parseFloat(e.target.value))}
            className="jo-xyz-demo-range-slider"
          />
          <div className="jo-xyz-demo-range-value">{(formData.minCapRate * 100).toFixed(1)}%</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="jo-xyz-demo-investor-signup-page">
      <div className="jo-xyz-demo-investor-signup-page__header">
        <h1>Investor Signup</h1>
        <p className="jo-xyz-demo-investor-signup-page__subtitle">
          Join our marketplace and start investing in commercial real estate opportunities
        </p>
      </div>

      <div className="jo-xyz-demo-investor-signup-page__content">
        <div className="jo-xyz-demo-signup-container">
          {/* Progress Indicator */}
          <div className="jo-xyz-demo-signup-progress">
            <div className="jo-xyz-demo-progress-steps">
              {[1, 2, 3].map((stepNumber) => (
                <div 
                  key={stepNumber}
                  className={`jo-xyz-demo-progress-step ${step >= stepNumber ? 'jo-xyz-demo-progress-step--active' : ''} ${step > stepNumber ? 'jo-xyz-demo-progress-step--completed' : ''}`}
                >
                  <div className="jo-xyz-demo-progress-step__number">
                    {step > stepNumber ? '✓' : stepNumber}
                  </div>
                  <div className="jo-xyz-demo-progress-step__label">
                    {stepNumber === 1 && 'Basic Info'}
                    {stepNumber === 2 && 'Profile'}
                    {stepNumber === 3 && 'Mandates'}
                  </div>
                </div>
              ))}
            </div>
            <div className="jo-xyz-demo-progress-bar">
              <div 
                className="jo-xyz-demo-progress-bar__fill"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>

          {/* Form Content */}
          <div className="jo-xyz-demo-signup-form">
            <div className="jo-xyz-demo-card">
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </div>

            {/* Navigation */}
            <div className="jo-xyz-demo-signup-navigation">
              <button 
                className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary"
                onClick={handleBack}
                disabled={step === 1}
              >
                Back
              </button>
              <button 
                className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                onClick={handleNext}
                disabled={!isStepValid()}
              >
                {step === 3 ? 'Create Account' : 'Next'}
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="jo-xyz-demo-signup-summary">
            <div className="jo-xyz-demo-card">
              <h3>Account Summary</h3>
              <div className="jo-xyz-demo-summary-content">
                <div className="jo-xyz-demo-summary-item">
                  <span className="jo-xyz-demo-summary-label">Organization:</span>
                  <span className="jo-xyz-demo-summary-value">{formData.name || 'Not specified'}</span>
                </div>
                <div className="jo-xyz-demo-summary-item">
                  <span className="jo-xyz-demo-summary-label">Type:</span>
                  <span className="jo-xyz-demo-summary-value">{formData.type}</span>
                </div>
                <div className="jo-xyz-demo-summary-item">
                  <span className="jo-xyz-demo-summary-label">Asset Types:</span>
                  <span className="jo-xyz-demo-summary-value">{formData.assetTypes.join(', ') || 'None selected'}</span>
                </div>
                <div className="jo-xyz-demo-summary-item">
                  <span className="jo-xyz-demo-summary-label">Max LTV:</span>
                  <span className="jo-xyz-demo-summary-value">{(formData.maxLtv * 100).toFixed(0)}%</span>
                </div>
                <div className="jo-xyz-demo-summary-item">
                  <span className="jo-xyz-demo-summary-label">Ticket Range:</span>
                  <span className="jo-xyz-demo-summary-value">
                    {formatCurrency(formData.minTicket)} - {formatCurrency(formData.maxTicket)}
                  </span>
                </div>
                <div className="jo-xyz-demo-summary-item">
                  <span className="jo-xyz-demo-summary-label">Geographic Focus:</span>
                  <span className="jo-xyz-demo-summary-value">{formData.geos.join(', ') || 'None selected'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorSignup;
